import { useQuery } from "@tanstack/react-query";

import { adminCustomersVehicles } from "@motolynk/api-client";
import { formatDateTime, useUiLabels, type CustomerTabProps } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { Badge, Card, CardContent, EmptyState, ErrorState, LoadingState } from "@motolynk/ui";

/** The admin customer page's "My car" tab: the vehicles the customer saved in the store (read-only). */
export function CustomerVehiclesTab({ customerUid }: CustomerTabProps) {
  const { t, i18n } = useTranslation("vehicles");
  const ui = useUiLabels();
  const rows = useQuery({
    queryKey: ["admin", "customers", customerUid, "vehicles"],
    queryFn: async () => (await adminCustomersVehicles(customerUid)).data,
  });
  if (rows.isPending) return <LoadingState />;
  if (rows.isError) {
    return (
      <ErrorState
        title={t("customerTab.loadFailed")}
        retryLabel={ui.retry}
        onRetry={() => void rows.refetch()}
      />
    );
  }
  if (rows.data.length === 0) return <EmptyState icon="car" title={t("customerTab.empty")} />;
  return (
    <ul className="grid gap-4 md:grid-cols-2">
      {rows.data.map((row) => (
        <li key={row.uid}>
          <Card>
            <CardContent className="space-y-1 pt-6 text-sm">
              <p className="flex flex-wrap items-center gap-2 font-medium">
                {row.vehicle.label}
                {row.is_default && <Badge tone="brand">{t("customerTab.default")}</Badge>}
              </p>
              {row.nickname && <p>{row.nickname}</p>}
              {row.registration_number && (
                <p className="text-muted-foreground">
                  {t("customerTab.registration", { number: row.registration_number })}
                </p>
              )}
              <p className="text-muted-foreground">
                {t("customerTab.added", { date: formatDateTime(row.created_at, i18n.language) })}
              </p>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}
